import React, { useState } from 'react';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import {
  HelpCircle,
  Plus,
  Trash2,
  Save,
  Eye,
  AlertCircle,
  Clock,
  Shuffle,
  Target
} from 'lucide-react';

const QuizEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    const validation = validateContent(updatedContent, 'quiz');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'quiz');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      question: 'Votre question ?',
      type: 'multiple-choice',
      options: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
      correct: 0,
      explanation: 'Explication de la réponse correcte',
      points: 1
    };
    
    const updatedQuestions = [...(content.questions || []), newQuestion];
    handleFieldChange('questions', updatedQuestions);
  };

  const updateQuestion = (questionId, updates) => {
    const updatedQuestions = (content.questions || []).map(question =>
      question.id === questionId ? { ...question, ...updates } : question
    );
    handleFieldChange('questions', updatedQuestions);
  };

  const removeQuestion = (questionId) => {
    const updatedQuestions = (content.questions || []).filter(question => question.id !== questionId);
    handleFieldChange('questions', updatedQuestions);
  };

  const addOption = (questionId) => {
    const question = (content.questions || []).find(q => q.id === questionId);
    if (question) {
      const newOptions = [...question.options, `Option ${question.options.length + 1}`];
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const updateOption = (questionId, optionIndex, value) => {
    const question = (content.questions || []).find(q => q.id === questionId);
    if (question) {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const removeOption = (questionId, optionIndex) => {
    const question = (content.questions || []).find(q => q.id === questionId);
    if (question && question.options.length > 2) {
      const newOptions = question.options.filter((_, index) => index !== optionIndex);
      const newCorrect = question.correct >= optionIndex ? Math.max(0, question.correct - 1) : question.correct;
      updateQuestion(questionId, { options: newOptions, correct: newCorrect });
    }
  };

  const duplicateQuestion = (questionId) => {
    const question = (content.questions || []).find(q => q.id === questionId);
    if (question) {
      const newQuestion = {
        ...question,
        id: Date.now(),
        question: `${question.question} (Copie)`
      };
      const updatedQuestions = [...(content.questions || []), newQuestion];
      handleFieldChange('questions', updatedQuestions);
    }
  };

  const getTotalPoints = () => {
    return (content.questions || []).reduce((total, question) => total + (question.points || 1), 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-yellow-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur de Quiz
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(content)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration du quiz */}
        <div className="space-y-6">
          {/* Informations générales */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Configuration du quiz
            </h3>
            
            <div className="space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Titre du quiz *
                </label>
                <input
                  type="text"
                  value={content.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="Ex: Quiz JavaScript - Les bases"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={content.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Décrivez l'objectif de ce quiz..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                />
              </div>

              {/* Limite de temps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Limite de temps (minutes)
                </label>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    value={content.timeLimit || ''}
                    onChange={(e) => handleFieldChange('timeLimit', parseInt(e.target.value) || 0)}
                    placeholder="30"
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  0 = pas de limite de temps
                </p>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-3">
              Statistiques du quiz
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-800 dark:text-blue-200">Nombre de questions :</span>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {(content.questions || []).length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-800 dark:text-blue-200">Points totaux :</span>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {getTotalPoints()}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-800 dark:text-blue-200">Durée estimée :</span>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {content.timeLimit ? `${content.timeLimit} min` : 'Illimitée'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Questions du quiz ({(content.questions || []).length})
            </h3>
            <button
              onClick={addQuestion}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une question
            </button>
          </div>

          <div className="space-y-4">
            {(content.questions || []).map((question, index) => (
              <div key={question.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Question {index + 1}
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => duplicateQuestion(question.id)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      title="Dupliquer"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removeQuestion(question.id)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Question */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Question
                    </label>
                    <textarea
                      value={question.question}
                      onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                    />
                  </div>

                  {/* Points */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Points
                      </label>
                      <input
                        type="number"
                        value={question.points || 1}
                        onChange={(e) => updateQuestion(question.id, { points: parseInt(e.target.value) || 1 })}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Type
                      </label>
                      <select
                        value={question.type}
                        onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="multiple-choice">Choix multiple</option>
                        <option value="single-choice">Choix unique</option>
                        <option value="true-false">Vrai/Faux</option>
                      </select>
                    </div>
                  </div>

                  {/* Options de réponse */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Options de réponse
                      </label>
                      <button
                        onClick={() => addOption(question.id)}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Ajouter
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {question.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`correct-${question.id}`}
                            checked={question.correct === optionIndex}
                            onChange={() => updateQuestion(question.id, { correct: optionIndex })}
                            className="text-primary"
                          />
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                          {question.options.length > 2 && (
                            <button
                              onClick={() => removeOption(question.id, optionIndex)}
                              className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Explication */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Explication (optionnelle)
                    </label>
                    <textarea
                      value={question.explanation}
                      onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                      rows="2"
                      placeholder="Expliquez pourquoi cette réponse est correcte..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Message si pas de questions */}
          {(content.questions || []).length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Aucune question
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Commencez par ajouter votre première question
              </p>
              <button
                onClick={addQuestion}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                Ajouter une question
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizEditor;
